import React, { useCallback, useState, useRef } from "react";
import { DA_Address } from "@danishagro/shared/src/interfaces/address.interface";
import { DA_AddressForm } from "@danishagro/shared/src/components/organisms/AddressForm/AddressForm.component";
import { ModalSize } from "@danishagro/shared/src/contexts/modal.context";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { useDynamicOverlay } from "@danishagro/shared/src/hooks/dynamicOverlay/useDynamicOverlay.hook";

export const useProductDeliveryAddresses = (
    setAddresses?: React.Dispatch<React.SetStateAction<DA_Address[]>>
) => {
    const { showOverlay, closeOverlay } = useDynamicOverlay();
    const { getDictionaryString } = useTranslations();
    const [viewState, setViewState] = useState<string>("NONE");

    // Store the current success callback in a ref so it's accessible in the submit handler
    const currentSuccessCallbackRef = useRef<(() => void) | undefined>();

    const onAddressFormSubmitted = useCallback(
        (newAddress: DA_Address) => {
            // Update the addresses list if provided
            if (setAddresses) {
                setAddresses((prev) => [...prev, newAddress]);
            }

            // Close the overlay
            closeOverlay();
            setViewState("NONE");

            // Call the success callback if provided (to close parent overlays)
            if (typeof currentSuccessCallbackRef.current === "function") {
                currentSuccessCallbackRef.current();
            }
        },
        [closeOverlay, setAddresses]
    );

    const onAddNewAddressClicked = useCallback(
        (eventOrCallback?: React.MouseEvent<HTMLButtonElement> | (() => void)) => {
            // Handle both event and callback signatures
            if (
                eventOrCallback &&
                typeof eventOrCallback === "object" &&
                "preventDefault" in eventOrCallback
            ) {
                // It's an event
                eventOrCallback.preventDefault();
                currentSuccessCallbackRef.current = undefined;
            } else if (typeof eventOrCallback === "function") {
                // It's a success callback
                currentSuccessCallbackRef.current = eventOrCallback;
            }

            setViewState("LOADING");

            // Show the address form overlay directly
            showOverlay(
                {
                    content: (
                        <DA_AddressForm
                            onSubmit={onAddressFormSubmitted}
                            submitText={getDictionaryString("siloAndHouses.addAddress")}
                        />
                    ),
                    title: getDictionaryString("siloAndHouses.addNewAddress"),
                    id: "add-delivery-address-overlay",
                },
                { modal: { size: ModalSize.LG } }
            );

            setViewState("NONE");
        },
        [showOverlay, onAddressFormSubmitted, getDictionaryString]
    );

    return {
        onAddNewAddressClicked,
        deliveryStatusModalViewState: viewState,
    };
};
